import { extractCustom } from "components/helpers";
import {
  useCustomFields,
  renderCustomFieldValue,
} from "components/CustomItems";
import { PanelSectionSkeleton } from "components/sidebar/sections/PanelSectionSkeleton";

// Read-only field primitives for a record's information panel, shared by every
// detail page so the two sidebar shapes present the same typography.

// `action` renders at the far right of the title row (e.g. a tag dropdown).
// While `isLoading`, the whole section, title included, is replaced by a skeleton.
export const PanelSection = ({
  title,
  action,
  children,
  isLoading = false,
}) => {
  if (isLoading) {
    return <PanelSectionSkeleton titled={!!title} />;
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:border-t tw:border-sidebar-border tw:pt-3 tw:first:border-t-0 tw:first:pt-0">
      {title && (
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <div className="tw:text-xs tw:font-medium tw:text-sidebar-foreground/70">
            {title}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
};

// Empty values render nothing rather than a blank row, matching the filter the
// legacy Sidebar applies to its info list.
// `action` renders at the far right of the label row (e.g. a tag dropdown).
export const PanelField = ({ label, children, action, inline = false }) => {
  const isEmpty =
    children == null ||
    children === "" ||
    (Array.isArray(children) && children.length === 0);

  if (isEmpty) {
    return null;
  }

  return (
    <div
      className={
        inline
          ? "tw:flex tw:items-center tw:justify-between tw:gap-2"
          : "tw:flex tw:flex-col tw:gap-0.5"
      }
    >
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <span className="tw:text-xs tw:text-sidebar-foreground/60">
          {label}
        </span>
        {action}
      </div>
      <div className="tw:text-xs tw:break-words tw:text-sidebar-foreground">
        {Array.isArray(children)
          ? children.map((value, index) => <div key={index}>{value}</div>)
          : children}
      </div>
    </div>
  );
};

// A record's custom fields as a panel section; renders nothing when there are none.
export const CustomFieldsPanelSection = ({
  title,
  items,
  modelType,
  isLoading = false,
}) => {
  const fields = extractCustom(items);
  // The per-model config is only needed to detect table-type values among the
  // fields present, so skip the fetch when the record has no custom fields.
  const customFields = useCustomFields(modelType, fields.length > 0);

  // The record's custom fields are unknown until it loads, so whether the
  // section exists at all is still undecided.
  if (isLoading) {
    return <PanelSectionSkeleton titled={!!title} />;
  }

  if (fields.length === 0) {
    return null;
  }

  return (
    <PanelSection title={title}>
      {fields.map(field => (
        <PanelField key={field.key} label={field.key}>
          {renderCustomFieldValue(field, customFields)}
        </PanelField>
      ))}
    </PanelSection>
  );
};
